import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { accountApi } from '../../services/accountApi';

/**
 * Mock customer portal — Bakeonator-hosted, not Stripe-hosted.
 */
function MockPortalPage() {
  const { activePartnerId } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scenario, setScenario] = useState('clear');

  const load = useCallback(async () => {
    if (!activePartnerId) return;
    try {
      const res = await accountApi.getPartnerBilling(activePartnerId);
      setData(res);
    } catch (e) {
      setError(e.message || 'Unable to load portal.');
    }
  }, [activePartnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!activePartnerId) {
    return <Alert variant="warning">Select a Partner workspace first.</Alert>;
  }
  if (!data && !error) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const sub = data?.subscription || {};
  const pm = data?.payment_method;

  return (
    <div className="console-page" style={{ maxWidth: 720 }}>
      <Alert variant="warning">
        <strong>Mock customer portal</strong> — Bakeonator test billing. This is not a Stripe-hosted
        portal. No real payment was processed.
      </Alert>
      <div className="d-flex justify-content-between mb-3">
        <h1 className="h4 mb-0">Manage Billing</h1>
        <Button as={Link} to="/console/account/billing" variant="outline-secondary" size="sm">
          Return to Partner Console
        </Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="h6">Current subscription</Card.Title>
          <p className="mb-1">
            Tier: <strong>{sub.selected_tier}</strong> · Status: {sub.billing_status}
          </p>
          <p className="mb-1">Interval: {sub.billing_interval}</p>
          <p className="mb-0">
            Next billing date:{' '}
            {sub.current_period_end
              ? new Date(sub.current_period_end).toLocaleDateString()
              : '—'}
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="h6">Payment method</Card.Title>
          <p>{pm ? pm.display : 'None on file'}</p>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={busy}
            onClick={() =>
              run(() =>
                accountApi.startBillingCheckout(activePartnerId, {
                  tier: 'pro',
                  interval: sub.billing_interval || 'monthly',
                })
              )
            }
          >
            Change plan / reopen checkout
          </Button>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="h6">Invoices</Card.Title>
          <Table size="sm" responsive>
            <tbody>
              {(data.invoices || []).slice(0, 5).map((inv) => (
                <tr key={inv.invoice_id}>
                  <td>{inv.invoice_number}</td>
                  <td>{inv.status}</td>
                  <td>${(inv.total_cents / 100).toFixed(2)}</td>
                </tr>
              ))}
              {(data.invoices || []).length === 0 && (
                <tr>
                  <td className="text-muted">No invoices</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="h6">Subscription actions</Card.Title>
          <div className="d-flex flex-wrap gap-2">
            {sub.cancel_at_period_end ? (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => run(() => accountApi.resumeBilling(activePartnerId))}
              >
                Resume
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline-danger"
                disabled={busy}
                onClick={() =>
                  run(() => accountApi.cancelBilling(activePartnerId, { at_period_end: true }))
                }
              >
                Cancel at period end
              </Button>
            )}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={busy}
              onClick={() =>
                run(() =>
                  accountApi.changeBillingPlan(activePartnerId, {
                    tier: 'free_indie',
                    interval: 'none',
                  })
                )
              }
            >
              Downgrade to Free
            </Button>
          </div>
        </Card.Body>
      </Card>

      {data?.mutations_allowed && (
        <Card>
          <Card.Body>
            <Card.Title className="h6">Development scenarios</Card.Title>
            <Form.Select
              className="mb-2"
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
            >
              <option value="clear">Clear / healthy</option>
              <option value="past_due">Past due</option>
              <option value="payment_failed">Payment failed</option>
            </Form.Select>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  run(() => accountApi.setMockBillingScenario(activePartnerId, { scenario }))
                }
              >
                Apply scenario
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={busy}
                onClick={() => run(() => accountApi.resetMockBilling(activePartnerId))}
              >
                Reset mock billing
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default MockPortalPage;
